"use client";

import { ConfirmationDialog } from "@/components/dialogs/Confirmation";
import type { FilterSelectActionInterface } from "@/components/list/FilterListComponent";
import { app } from "@/hooks/layout-context";
import type { BulkListActionResult } from "@/types/bulk-list-action";
import { Archive, Trash2 } from "lucide-react";
import { useContext, useRef, useState } from "react";
import { toast } from "sonner";
export function useBulkListAction<Row>({
  action,
  noun,
  consequences,
  getId,
  execute,
  invalidate,
  enabled = true
}: {
  action: "Delete" | "Archive";
  noun: string;
  consequences: string;
  getId: (row: Row) => string | null;
  execute: (ids: string[]) => Promise<BulkListActionResult>;
  invalidate: () => Promise<unknown>;
  enabled?: boolean;
}) {
  const organization = useContext(app("[organization-slug]"));
  const isAdmin = organization?.activeOrganizationMembership.role.isAdmin === true;
  const [selection, setSelection] = useState<{
    ids: string[];
    clear: () => void;
  } | null>(null);
  const [result, setResult] = useState<BulkListActionResult | null>(null);
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const close = () => {
    if (inFlight.current) return;
    setSelection(null);
    setResult(null);
  };
  const confirm = async () => {
    if (!selection || inFlight.current || !isAdmin || !enabled) return;
    if (result) {
      close();
      return;
    }
    inFlight.current = true;
    setPending(true);
    try {
      const outcome = await execute(selection.ids);
      selection.clear();
      if (outcome.failures.length) {
        setResult(outcome);
      } else {
        toast.success(`${action === "Delete" ? "Deleted" : "Archived"} ${outcome.succeededIds.length} ${noun}.`);
        setSelection(null);
      }
    } catch (error) {
      selection.clear();
      setSelection(null);
      toast.error(error instanceof Error ? `${error.message} Refresh and check the selected records before trying again.` : "The outcome could not be confirmed. Refresh and check the selected records before trying again.");
    } finally {
      try {
        await invalidate();
      } catch {
        toast.error("The list could not refresh. Reload it before selecting records again.");
      }
      inFlight.current = false;
      setPending(false);
    }
  };
  const selectAction: FilterSelectActionInterface<Row> = {
    label: action,
    icon: action === "Delete" ? <Trash2 /> : <Archive />,
    variant: "destructive",
    supportsAll: false,
    isDisabled: pending,
    onClick(target, clearSelection = () => {}) {
      if (!isAdmin || !enabled || inFlight.current || target.type !== "rows") return;
      const ids = target.rows.map(getId);
      if (ids.some(id => !id)) {
        toast.error("Some selected rows do not support this action. Select eligible records only.");
        return;
      }
      const selectedIds = [...new Set(ids.filter((id_0): id_0 is string => id_0 !== null))];
      if (!selectedIds.length || selectedIds.length > 100) {
        toast.error(`Select between 1 and 100 ${noun}.`);
        return;
      }
      setResult(null);
      setSelection({
        ids: selectedIds,
        clear: clearSelection
      });
    }
  };
  return {
    selectActions: isAdmin && enabled ? [selectAction] : [],
    dialog: selection && <ConfirmationDialog open onOpenChange={open => {
      if (!open) close();
    }} title={result ? `${action} result` : `${action} ${selection.ids.length} ${noun}?`} description={result ? `${result.succeededIds.length} confirmed complete; ${result.failures.length} not confirmed. Successful changes were not rolled back. Refresh and check the remaining records before selecting again.` : consequences} content={result && <ul className="max-h-60 overflow-auto break-words text-sm">{result.failures.map(failure => <li key={failure.id}>{failure.id}: {failure.message}</li>)}</ul>} confirmLabel={result ? "Done" : action} destructive={!result} isLoading={pending} confirmDisabled={!isAdmin || !enabled} onConfirm={() => void confirm()} />
  };
}